import React, { useEffect, useState } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  FormField,
  TextInput,
  Select,
  StackLayout,
} from '@leagueplatform/genesis-core';
import {
  stateOption,
  isEmpty,
  isNotEmpty,
  updateFormData,
  isNoSpace,
  isValidZipCodeLength,
} from '../../../helpers';
import { AttachmentReusableComponent } from '../../../../reusable-components/attachment.component';

export function OtherCoverageInsuranceCompanyInformation(props: any) {
  const {
    errorHandling,
    nextButtonClicked,
    footerToBottom,
    setIsAttaachmentLoading,
  } = props;
  const [form, setForm] = useState({
    otherInsuranceCompany: '',
    otherInsuranceAddressLine1: '',
    otherInsuranceAddressLine2: '',
    otherInsuranceAddressCity: '',
    otherInsuranceAddressState: '',
    otherInsuranceAddressZipCode: '',
    eobAttachments: [],
  });
  const [errors, setErrors] = useState({
    otherInsuranceCompany: false,
    otherInsuranceAddressZipCode: false,
  });

  const [zipCodeError, setZipCodeError] = useState<string>('');
  const [isAttachmentError, setIsAttachmentError] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');

  const formField = isEmpty(form.otherInsuranceCompany);

  const isValid =
    isNotEmpty(form.otherInsuranceCompany) && form.eobAttachments.length !== 0;
  useEffect(() => {
    footerToBottom(false);
  }, [footerToBottom]);
  useEffect(() => {
    if (
      formField ||
      form.eobAttachments.length === 0 ||
      errors.otherInsuranceAddressZipCode
    ) {
      errorHandling(false);
      if (isValidZipCodeLength(form.otherInsuranceAddressZipCode)) {
        setZipCodeError('Please enter a valid zip code.');
      } else {
        setZipCodeError('');
      }
    } else {
      errorHandling(true);
    }
  }, [formField, errorHandling, isAttachmentError, form, errors]);

  useEffect(() => {
    if (nextButtonClicked) {
      if (!isValid) {
        setErrors({
          otherInsuranceCompany: isEmpty(form.otherInsuranceCompany),
          otherInsuranceAddressZipCode: isNotEmpty(
            form.otherInsuranceAddressZipCode,
          )
            ? isValidZipCodeLength(form.otherInsuranceAddressZipCode)
            : false,
        });
        if (form.eobAttachments.length === 0) {
          setIsAttachmentError(true);
          setAttachmentError('Attachment is required.');
        }
      }
    }
  }, [nextButtonClicked, isValid, form]);

  const updateErrorState = (value: string, id: string) => {
    if (id === 'otherInsuranceAddressZipCode') {
      if (isValidZipCodeLength(value)) {
        setZipCodeError('Please enter a valid zip code.');
        setErrors((prevState) => ({
          ...prevState,
          otherInsuranceAddressZipCode: true,
        }));
      } else {
        setZipCodeError('');
        setErrors((prevState) => ({
          ...prevState,
          otherInsuranceAddressZipCode: false,
        }));
      }
    } else {
      const nextErrorState = {
        ...errors,
        [id]: isEmpty(value),
      };
      setErrors(nextErrorState);
    }
  };

  const onUpdateField = (event: any) => {
    const { value, id } = event.target;
    const nextFormState = {
      ...form,
      [event.target.id]: /^\s/.test(event.target.value)
        ? ''
        : event.target.value,
    };
    setForm(nextFormState);

    if (id === 'otherInsuranceCompany') {
      setForm((prevState) => ({
        ...prevState,
        otherInsuranceCompany: isNoSpace(value)
          ? ''
          : value?.replace(/[^a-zA-Z0-9 \-']+/gi, ''),
      }));
      updateErrorState(event.target.value, event.target.id);
    }

    if (id === 'otherInsuranceAddressCity') {
      setForm((prevState) => ({
        ...prevState,
        otherInsuranceAddressCity: isNoSpace(value)
          ? ''
          : value?.replace(/[^a-zA-Z ]+/gi, ''),
      }));
    }

    if (id === 'otherInsuranceAddressZipCode') {
      setForm((prevState) => ({
        ...prevState,
        otherInsuranceAddressZipCode: isNoSpace(value)
          ? ''
          : value?.replace(/[^0-9]+/gi, ''),
      }));
      updateErrorState(event.target.value, event.target.id);
    }
  };

  const attachmentsAdded = (attachmentsObj: any) => {
    const nextFormState = {
      ...form,
      eobAttachments: attachmentsObj,
    };
    setForm(nextFormState);
    setIsAttachmentError(false);
    setAttachmentError('');
  };

  useEffect(() => {
    const pInfo = JSON.parse(sessionStorage.getItem('formData') || '{}');
    if (pInfo) {
      setForm({
        otherInsuranceCompany: pInfo?.otherInsuranceCompany,
        otherInsuranceAddressLine1: pInfo?.otherInsuranceAddressLine1,
        otherInsuranceAddressLine2: pInfo?.otherInsuranceAddressLine2,
        otherInsuranceAddressCity: pInfo?.otherInsuranceAddressCity,
        otherInsuranceAddressState: pInfo?.otherInsuranceAddressState,
        otherInsuranceAddressZipCode: pInfo?.otherInsuranceAddressZipCode,
        eobAttachments: pInfo?.eobAttachments || [],
      });
    }
  }, []);

  const onBlurField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  const onKeyPressField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  useEffect(() => {
    if (form) {
      updateFormData(form);
    }
  }, [form]);

  return (
    <StackLayout
      css={
        {
          backgroundColor: 'White',
          maxWidth: '800px',
        } as GDSStyleObject
      }
      horizontalAlignment="center"
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$three $two $two $two',
          } as GDSStyleObject
        }
      >
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$none $two',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="1"
            size={{
              '@initial': 'xxl',
              '@mobile': 'xl',
              '@mobileLandscape': 'xl',
              '@tablet': 'xl',
              '@laptop': 'xxl',
              '@desktop': 'xxl',
            }}
          >
            Member Dental Claim Form
          </HeadingText>
        </Box>
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$one $two',
              width: '100%',
            } as GDSStyleObject
          }
        >
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
              } as GDSStyleObject
            }
          >
            <HeadingText
              level="3"
              data-testid="step4"
              size={{
                '@initial': 'xl',
                '@mobile': 'lg',
                '@mobileLandscape': 'lg',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
            >
              Step 4: Other Coverage
            </HeadingText>
          </Box>
          <Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="otherInsuranceCompany"
                label="Name of Other Insurance Company"
                statusIconLabel="Error"
                inputStatus={errors.otherInsuranceCompany ? 'error' : undefined}
                statusMessage={
                  errors.otherInsuranceCompany
                    ? 'Insurance company name is required.'
                    : ''
                }
              >
                <TextInput
                  id="otherInsuranceCompany"
                  name="otherInsuranceCompany"
                  value={form.otherInsuranceCompany}
                  onChange={onUpdateField}
                  maxLength={50}
                  onBlur={onBlurField}
                  onKeyPress={onKeyPressField}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="otherInsuranceAddressLine1"
                label="Company Address Line 1 (Optional)"
              >
                <TextInput
                  name="otherInsuranceAddressLine1"
                  value={form.otherInsuranceAddressLine1}
                  onChange={onUpdateField}
                  maxLength={50}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="otherInsuranceAddressLine2"
                label="Company Address Line 2 (Optional)"
              >
                <TextInput
                  name="otherInsuranceAddressLine2"
                  value={form.otherInsuranceAddressLine2}
                  onChange={onUpdateField}
                  maxLength={50}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField id="otherInsuranceAddressCity" label="City (Optional)">
                <TextInput
                  name="otherInsuranceAddressCity"
                  value={form.otherInsuranceAddressCity}
                  onChange={onUpdateField}
                  maxLength={50}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="otherInsuranceAddressState"
                label="State (Optional)"
              >
                <Select
                  name="otherInsuranceAddressState"
                  options={stateOption()}
                  placeholder="Please Select"
                  id="otherInsuranceAddressState"
                  value={form.otherInsuranceAddressState}
                  onChange={onUpdateField}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="otherInsuranceAddressZipCode"
                label="Zip Code (Optional)"
                statusIconLabel="Error"
                inputStatus={
                  errors.otherInsuranceAddressZipCode ? 'error' : undefined
                }
                statusMessage={
                  errors.otherInsuranceAddressZipCode ? zipCodeError : ''
                }
              >
                <TextInput
                  pattern="[0-9]*"
                  name="otherInsuranceAddressZipCode"
                  value={form.otherInsuranceAddressZipCode}
                  onChange={onUpdateField}
                  maxLength={5}
                />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="eobAttachment"
                label=""
                statusIconLabel="Error"
                inputStatus={isAttachmentError ? 'error' : undefined}
                statusMessage={isAttachmentError ? attachmentError : ''}
              >
                <AttachmentReusableComponent
                  displayText="Attach Explanation of Benefits"
                  acceptTypes={['.pdf', '.doc', '.png', '.gif', '.jpg']}
                  onUpdate={attachmentsAdded}
                  attachments={form.eobAttachments}
                  serviceIndex="dental-itemized-bill"
                  setIsAttaachmentLoading={setIsAttaachmentLoading}
                />
              </FormField>
            </Box>
          </Box>
        </Box>
      </Box>
    </StackLayout>
  );
}
